// LearnCore Frontend - 学习集查看页面

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import Loading from '../components/Loading';
import ImageDisplay from '../components/ImageDisplay';
import ClozeRenderer, { hasClozeNotation } from '../components/ClozeRenderer';
import TagEditor from '../components/TagEditor';
import type { StudySetDetails, Flashcard } from '../types';
import { 
  PlayIcon,
  PencilIcon,
  ShareIcon,
  UserIcon,
  CalendarIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowLeftIcon,
  BookOpenIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export default function SetViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [studySetDetails, setStudySetDetails] = useState<StudySetDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 编辑相关状态
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editingCard, setEditingCard] = useState<Flashcard | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadStudySet(id);
    }
  }, [id]);

  const loadStudySet = async (setId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getStudySet(setId);
      // 确保每个卡片的 tags 字段是数组
      if (response && response.set && response.set.flashcards) {
        response.set.flashcards = response.set.flashcards.map((card: any) => ({
          ...card,
          tags: Array.isArray(card.tags) ? card.tags : []
        }));
      }
      setStudySetDetails(response);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('学习集不存在');
      } else if (err.response?.status === 403) {
        setError('无权访问此学习集');
      } else {
        setError(err.response?.data?.error || '获取学习集失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 开始编辑卡片
  const startEditCard = (card: Flashcard) => {
    setEditingCardId(card.id);
    setEditingCard({ ...card });
  };

  // 取消编辑
  const cancelEditCard = () => {
    setEditingCardId(null);
    setEditingCard(null);
  };

  // 保存卡片编辑
  const saveCardEdit = async () => {
    if (!id || !editingCard) return;
    
    try {
      setSaving(true);
      await apiClient.updateCard(id, editingCard.id, {
        term: editingCard.term.trim(),
        definition: editingCard.definition.trim(),
        term_image_url: editingCard.term_image_url,
        definition_image_url: editingCard.definition_image_url,
        tag_ids: editingCard.tags?.map(tag => tag.id) || []
      });
      
      // 重新加载数据
      await loadStudySet(id);
      setEditingCardId(null);
      setEditingCard(null);
    } catch (err: any) {
      console.error('保存卡片失败:', err);
      alert('保存卡片失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 处理卡片字段变化
  const handleCardFieldChange = (field: keyof Flashcard, value: any) => {
    if (!editingCard) return;
    setEditingCard({ ...editingCard, [field]: value });
  };

  const handleShare = async () => {
    if (!is_owner) {
      alert('只有学习集的创建者才能分享学习集');
      return;
    }

    try {
      // 如果学习集还未公开，先设置为公开
      if (!set.is_public) {
        await apiClient.updateStudySet(set.id, { is_public: true });
        // 重新加载学习集数据以更新状态
        await loadStudySet(set.id);
      }
      
      // 复制分享链接
      const url = `${window.location.origin}/sets/${id}/view`;
      await navigator.clipboard.writeText(url);
      alert(set.is_public 
        ? '分享链接已复制到剪贴板！' 
        : '学习集已设置为公开并且链接已复制到剪贴板！其他用户现在可以在学习集市场中看到它。'
      );
    } catch (err: any) {
      if (err.response?.data?.error) {
        alert(`分享失败：${err.response.data.error}`);
      } else {
        // 如果是复制链接失败，还是让用户手动复制
        const url = `${window.location.origin}/sets/${id}/view`;
        prompt('学习集已设置为公开！请复制下面的链接:', url);
      }
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 智能返回函数 - 根据来源决定返回到哪里
  const handleGoBack = () => {
    // 检查是否有来源信息
    const from = location.state?.from;
    
    if (typeof from === 'string') {
      // 如果来源是学习模式或测验页面，避免回到学习页造成循环，返回仪表盘
      if (
        from.includes('/study') ||
        from.includes('/quiz/term') ||
        from.includes('/quiz/definition')
      ) {
        navigate('/dashboard');
        return;
      }
      // 否则按来源返回
      navigate(from);
      return;
    } else {
      // 检查浏览器历史记录
      const historyLength = window.history.length;
      
      // 如果历史记录很少，可能是直接访问的链接，返回到首页
      if (historyLength <= 2) {
        navigate('/');
        return;
      }
      
      // 检查上一个页面是否是学习模式页面
      const referrer = document.referrer;
      if (referrer) {
        const referrerPath = new URL(referrer).pathname;
        
        // 如果是从学习模式页面来的，返回到仪表盘
        if (referrerPath.includes('/study') || 
            referrerPath.includes('/quiz/term') || 
            referrerPath.includes('/quiz/definition')) {
          navigate('/dashboard');
          return;
        }
        
        // 如果是从市场页面来的，返回到市场
        if (referrerPath.includes('/marketplace')) {
          navigate('/marketplace');
          return;
        }
      }
      
      // 默认返回到仪表盘
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading size="lg" text="加载学习集..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <BookOpenIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error}
          </h1>
          <p className="text-gray-600 mb-6">
            请检查链接是否正确，或者联系学习集的创建者。
          </p>
          <button
            onClick={handleGoBack}
            className="btn-outline mr-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            返回
          </button>
          <Link to="/" className="btn-primary">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  if (!studySetDetails) {
    return null;
  }

  const { set, author, is_owner } = studySetDetails;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <button
          onClick={handleGoBack}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          返回
        </button>

        

        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {set.title}
            </h1>
            {set.description && (
              <p className="text-gray-600 mb-4">
                {set.description}
              </p>
            )}
          </div>

          <div className="flex space-x-2 ml-6">
            <div className="flex items-center space-x-1">
              <button
                onClick={handleShare}
                className={`btn-outline ${
                  set.is_public 
                    ? 'border-green-500 text-green-600 hover:bg-green-50' 
                    : ''
                }`}
              >
                <ShareIcon className="h-4 w-4 mr-1" />
                {set.is_public ? '已分享' : '分享'}
              </button>
              
              <div className="relative group">
                <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                
                {/* Tooltip */}
                <div className="absolute right-0 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                  <div className="mb-1 font-medium">
                    {set.is_public ? '学习集已公开分享' : '分享学习集'}
                  </div>
                  <div>
                    {set.is_public 
                      ? '其他用户可以在学习集市场中找到并学习这个学习集。点击可以复制分享链接。' 
                      : '将学习集设为公开，让其他用户在学习集市场中找到并学习。'
                    }
                  </div>
                  {/* Tooltip arrow */}
                  <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
            
            {is_owner && (
              <Link
                to={`/sets/${set.id}/edit`}
                className="btn-outline"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                编辑
              </Link>
            )}
          </div>
        </div>
        {/* 元信息 */}
        <div className="flex items-center space-x-16 text-sm text-gray-500 mb-6">
          {author && (
            <div className="flex items-center">
              <UserIcon className="h-4 w-4 mr-1" />
              <span>{author.email}</span>
            </div>
          )}
          
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 mr-1" />
            <span>创建于 {formatDate(set.created_at)}</span>
          </div>
          
          <div className="flex items-center">
            {set.is_public ? (
              <>
                <EyeIcon className="h-4 w-4 mr-1 text-green-600" />
                <span className="text-green-600 font-medium">公开分享</span>
              </>
            ) : (
              <>
                <EyeSlashIcon className="h-4 w-4 mr-1" />
                <span>私人</span>
              </>
            )}
          </div>
          
          <div className="flex items-center">
            <BookOpenIcon className="h-4 w-4 mr-1" />
            <span>{set.flashcards.length} 张卡片</span>
          </div>
        </div>
      </div>

      {/* 学习模式选择 */}
      {set.flashcards.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
            选择学习模式
          </h3>
          
          <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {/* 抽认卡模式 */}
            <Link
              to={`/sets/${set.id}/study`}
              className="block p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-200">
                  <PlayIcon className="h-6 w-6" />
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">抽认卡模式</h4>
                <p className="text-sm text-gray-600">
                  翻转卡片学习术语和定义，标记掌握程度
                </p>
              </div>
            </Link>

            {/* 术语测验模式 */}
            <Link
              to={`/sets/${set.id}/quiz/term`}
              className={`block p-6 bg-white border-2 border-gray-200 rounded-lg transition-all group ${
                set.flashcards.length >= 4 
                  ? 'hover:border-blue-500 hover:bg-blue-50' 
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={(e) => {
                if (set.flashcards.length < 4) {
                  e.preventDefault();
                }
              }}
            >
              <div className="text-center">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${
                  set.flashcards.length >= 4 
                    ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-200' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  <AcademicCapIcon className="h-6 w-6" />
                </div>
                <h4 className={`text-lg font-medium mb-2 ${
                  set.flashcards.length >= 4 ? 'text-gray-900' : 'text-gray-400'
                }`}>
                  术语测验
                </h4>
                <p className={`text-sm ${
                  set.flashcards.length >= 4 ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  看定义选择正确的术语
                  {set.flashcards.length < 4 && <br />}
                  {set.flashcards.length < 4 && <span className="text-red-500">(需要至少4张卡片)</span>}
                </p>
              </div>
            </Link>

            {/* 定义测验模式 */}
            <Link
              to={`/sets/${set.id}/quiz/definition`}
              className={`block p-6 bg-white border-2 border-gray-200 rounded-lg transition-all group ${
                set.flashcards.length >= 4 
                  ? 'hover:border-green-500 hover:bg-green-50' 
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={(e) => {
                if (set.flashcards.length < 4) {
                  e.preventDefault();
                }
              }}
            >
              <div className="text-center">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${
                  set.flashcards.length >= 4 
                    ? 'bg-green-100 text-green-600 group-hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  <QuestionMarkCircleIcon className="h-6 w-6" />
                </div>
                <h4 className={`text-lg font-medium mb-2 ${
                  set.flashcards.length >= 4 ? 'text-gray-900' : 'text-gray-400'
                }`}>
                  定义测验
                </h4>
                <p className={`text-sm ${
                  set.flashcards.length >= 4 ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  看术语选择正确的定义
                  {set.flashcards.length < 4 && <br />}
                  {set.flashcards.length < 4 && <span className="text-red-500">(需要至少4张卡片)</span>}
                </p>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* 卡片列表 */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          抽认卡内容
        </h2>
        
        {set.flashcards.length === 0 ? (
          <div className="text-center py-8">
            <BookOpenIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">此学习集还没有卡片</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {set.flashcards.map((card, index) => {
              const isEditing = editingCardId === card.id;
              const currentCard = isEditing ? editingCard : card;
              
              return (
                <div key={card.id} className="border border-gray-200 rounded-lg p-6 bg-white">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    
                    <div className="flex-1">
                      {/* 编辑按钮 */}
                      {is_owner && !isEditing && (
                        <div className="flex justify-end mb-4">
                          <button
                            onClick={() => startEditCard(card)}
                            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            编辑
                          </button>
                        </div>
                      )}
                      
                      {/* 编辑态控制按钮 */}
                      {isEditing && (
                        <div className="flex justify-end mb-4 space-x-2">
                          <button
                            onClick={saveCardEdit}
                            disabled={saving}
                            className="flex items-center px-3 py-1 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50"
                          >
                            <CheckIcon className="h-4 w-4 mr-1" />
                            {saving ? '保存中...' : '保存'}
                          </button>
                          <button
                            onClick={cancelEditCard}
                            disabled={saving}
                            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            取消
                          </button>
                        </div>
                      )}
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">术语</h3>
                          {currentCard?.term_image_url && (
                            <div className="mb-2">
                              <ImageDisplay 
                                imageUrl={currentCard.term_image_url} 
                                alt="术语图片"
                                size="sm"
                              />
                            </div>
                          )}
                          {isEditing ? (
                            <textarea
                              value={currentCard?.term || ''}
                              onChange={(e) => handleCardFieldChange('term', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                              rows={3}
                              disabled={saving}
                            />
                          ) : (
                            <ClozeRenderer 
                              content={card.term} 
                              showCloze={hasClozeNotation(card.term)}
                              className="text-gray-900"
                            />
                          )}
                        </div>
                        
                        <div>
                          <h3 className="text-sm font-medium text-gray-500 mb-1">定义</h3>
                          {currentCard?.definition_image_url && (
                            <div className="mb-2">
                              <ImageDisplay 
                                imageUrl={currentCard.definition_image_url} 
                                alt="定义图片"
                                size="sm"
                              />
                            </div>
                          )}
                          {isEditing ? (
                            <textarea
                              value={currentCard?.definition || ''}
                              onChange={(e) => handleCardFieldChange('definition', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                              rows={3}
                              disabled={saving}
                            />
                          ) : (
                            <ClozeRenderer 
                              content={card.definition} 
                              showCloze={hasClozeNotation(card.definition)}
                              className="text-gray-900"
                            />
                          )}
                        </div>
                      </div>
                      
                      {/* 标签显示和编辑 */}
                      <div className="mt-4">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">标签</h3>
                        {isEditing ? (
                          <TagEditor
                            initialTags={currentCard?.tags || []}
                            onTagsChange={(tagIds, tags) => {
                              if (tags && tags.length > 0) {
                                handleCardFieldChange('tags', tags);
                              } else {
                                // 回退逻辑
                                const fallbackTags = tagIds.map(id => {
                                  const existingTag = (currentCard?.tags || []).find(tag => tag.id === id);
                                  if (existingTag) return existingTag;
                                  return { id, name: id, type: 'custom' as const };
                                });
                                handleCardFieldChange('tags', fallbackTags);
                              }
                            }}
                            disabled={saving}
                          />
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {Array.isArray(card.tags) && card.tags.length > 0 ? (
                              card.tags.map(tag => (
                                <span
                                  key={tag.id}
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    tag.type === 'grade' 
                                      ? 'bg-blue-100 text-blue-800'
                                      : tag.type === 'subject'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {tag.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-gray-500">无标签</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
